import { TimelineAction, TimelineRow } from '@xzdarcy/react-timeline-editor';
import {
  Material,
  MaterialDetail,
  MaterialType,
  ProjectMode,
  SubtitleSegmented,
} from '@/interface.ts';
import WaveSurfer from 'wavesurfer.js';

export enum RowType {
  audioSubtitle = 31,
  translationSubtitle = 32,
  video = 0,
  audio = 1,
  music = 2,
  translation = 4,
}

export interface CustomAction extends TimelineAction {
  materialId: number;
  type: RowType;
  hide?: boolean;
  volume?: number;
  oriDuration?: number;
  minDuration?: number;
  maxDuration?: number;
  speed?: number;
  text?: string;
  from?: number;
  to?: number;
  url?: string;
  vttUrl?: string;
  vttString?: string;
  speakerId?: number;
  trackIndex?: number;
  loading?: boolean;
  mute?: boolean;
  subtitle_start?: number;
  subtitle_end?: number;
  sentenceId?: number;
  index?: number;
  isEdit?: boolean;
}

export interface CustomRow extends TimelineRow {
  type: RowType;
  actions: CustomAction[];
  hide?: boolean;
  volume?: number;
  trackIndex?: number;
  mute?: boolean;
}

export enum AudioMode {
  Origin, // 仅有原声
  Translation, // 翻译声
  OriInTrans, //翻译模式下的原声
}

export const TypeMap = {
  [RowType.video]: MaterialType.VIDEO,
  [RowType.audio]: MaterialType.AUDIO,
  [RowType.music]: MaterialType.MUSIC,
  [RowType.translation]: MaterialType.TRANSLATION,
  [RowType.audioSubtitle]: MaterialType.CAPTIONS,
  [RowType.translationSubtitle]: MaterialType.CAPTIONS,
};

export const isVideoType = (item: CustomAction | CustomRow) => item.type === RowType.video;
export const isSubtitle = (item: CustomAction | CustomRow) =>
  item.type === RowType.audioSubtitle || item.type === RowType.translationSubtitle;
export const isMusicType = (item: CustomAction | CustomRow) => item.type === RowType.music;
export const isAudioType = (item: CustomAction | CustomRow) => item.type === RowType.audio;
export const isAudioSubtitleType = (item: CustomAction | CustomRow) =>
  item.type === RowType.audioSubtitle;
export const isTranslationType = (item: CustomAction | CustomRow) =>
  item.type === RowType.translation;
export const isTranslationSubtitleType = (item: CustomAction | CustomRow) =>
  item.type === RowType.translationSubtitle;

// 是否同时展示译文句子和译文字幕 模式A
export const showAudioAndTranslation = (mode: ProjectMode) => mode === ProjectMode.TRANSLATION_TTS;
// 是否展示原文句子和译文字幕 模式B
export const showAudioAndDoubleSubtitle = (mode: ProjectMode) => mode === ProjectMode.TRANSLATION;
// 是否为旁白模式
export const isNarrationMode = (mode: ProjectMode) => mode === ProjectMode.NARRATION;
// 模式D
export const isProjectModelD = (mode: ProjectMode) => mode === ProjectMode.SUBTITLE;
// 模式E
export const isProjectModelE = (mode: ProjectMode) => mode === ProjectMode.SUBTITLE_TRANSLATION;
// 是否为译文字幕
export const isTranslationSubtitle = (mode: ProjectMode) => showAudioAndTranslation(mode);
// 字幕是否可以拖动
export const canSubtitleMove = (mode: ProjectMode) =>
  isProjectModelD(mode) || isProjectModelE(mode) || showAudioAndDoubleSubtitle(mode);
// 字幕是否需要同步
export const canSyncSubtitle = (mode: ProjectMode) =>
  showAudioAndTranslation(mode) || showAudioAndDoubleSubtitle(mode) || isProjectModelE(mode);
let selfAddIndex = 1;
export function generateId() {
  return generateMaterialId().toString(16);
}

export function generateMaterialId() {
  const randomOffset = 649257384 + selfAddIndex++;
  return Date.now() + randomOffset;
}

export function materialToAction(
  material: MaterialDetail,
  mode?: AudioMode,
  projectMode?: ProjectMode,
) {
  let action = {};
  const {
    id,
    timeLineFrom,
    timeLineTo,
    from,
    to,
    sentence,
    sentenceUrl,
    speakerId,
    url,
    text,
    speed,
    volume,
    hide,
    vttUrl,
    vttString,
    trackIndex,
    generating,
    mute,
    wordId,
    isEdit,
  } = material;
  switch (material.type) {
    case MaterialType.VIDEO: {
      action = {
        id: generateId(),
        materialId: id,
        type: RowType.video,
        start: timeLineFrom / 1000,
        end: timeLineTo / 1000,
        from: from / 1000,
        to: to / 1000,
        oriDuration: (to - from) / 1000,
        url,
        hide,
        speed,
        volume,
        vttUrl,
        vttString,
        trackIndex,
        effectId: 'effect0',
      };
      break;
    }
    case MaterialType.MUSIC: {
      action = {
        id: generateId(),
        materialId: id,
        type: RowType.music,
        start: timeLineFrom / 1000,
        end: timeLineTo / 1000,
        from: from / 1000,
        to: to / 1000,
        oriDuration: (to - from) / 1000,
        minDuration: (to - from) / 8000,
        maxDuration: (to - from) / 1000,
        url,
        hide,
        speed,
        volume,
        trackIndex,
        effectId: 'effect1',
        mute,
      };
      break;
    }
    case MaterialType.TRANSLATION:
    case MaterialType.AUDIO: {
      action = {
        id: generateId(),
        materialId: id,
        type: mode === AudioMode.Translation ? RowType.translation : RowType.audio,
        start: timeLineFrom / 1000,
        end: timeLineTo / 1000,
        from: from / 1000,
        to: to / 1000,
        movable: mode !== AudioMode.OriInTrans,
        flexible: mode !== AudioMode.OriInTrans,
        oriDuration: (to - from) / 1000,
        minDuration: (to - from) / 8000,
        maxDuration: (to - from) / 125,
        url: sentenceUrl,
        text: sentence,
        speakerId,
        hide,
        speed,
        volume,
        trackIndex,
        loading: generating,
        effectId: 'effect1',
        mute,
        isEdit,
      };
      break;
    }
    case MaterialType.CAPTIONS: {
      action = {
        id: generateId(),
        materialId: wordId || id, //物料的id
        sentenceId: id, // 句子的id
        type: mode === AudioMode.Translation ? RowType.translationSubtitle : RowType.audioSubtitle,
        start: timeLineFrom / 1000,
        end: timeLineTo / 1000,
        movable: projectMode && canSubtitleMove(projectMode),
        flexible: mode !== AudioMode.OriInTrans,
        text,
        effectId: 'effect2',
      };
      break;
    }
  }
  return action as CustomAction;
}

export function actionToMaterial(action: CustomAction) {
  let material = {};
  const {
    type,
    materialId,
    start,
    end,
    from,
    to,
    url,
    speed,
    volume,
    hide,
    vttUrl,
    vttString,
    text,
    speakerId,
    trackIndex,
  } = action as Required<CustomAction>;
  switch (type) {
    case RowType.music: {
      material = {
        id: materialId,
        type: MaterialType.MUSIC,
        timeLineFrom: start * 1000,
        timeLineTo: end * 1000,
        from: from * 1000,
        to: to * 1000,
        url,
        speed,
        volume,
        trackIndex,
      };
      break;
    }
    case RowType.video: {
      material = {
        id: materialId,
        type: MaterialType.VIDEO,
        timeLineFrom: start * 1000,
        timeLineTo: end * 1000,
        from: from * 1000,
        to: to * 1000,
        url,
        vttUrl,
        vttString,
        hide,
        trackIndex,
      };
      break;
    }
    case RowType.audio: {
      material = {
        id: materialId,
        type: MaterialType.AUDIO,
        timeLineFrom: start * 1000,
        timeLineTo: end * 1000,
        from: from * 1000,
        to: to * 1000,
        sentenceUrl: url,
        sentence: text,
        speakerId,
        speed,
        volume,
        trackIndex,
        subtitle: {
          id: materialId,
          type: MaterialType.CAPTIONS,
          text: text,
          timeLineFrom: start * 1000,
          timeLineTo: end * 1000,
        },
      };
      break;
    }
  }
  return material as MaterialDetail;
}

export function transformVideos(videos: Material) {
  const videoRows: CustomRow[] = [];
  const { details, oriUrl } = videos;
  if (details && details.length > 0) {
    const row: CustomRow = {
      id: generateId(),
      type: RowType.video,
      actions: [],
      rowHeight: 42,
      classNames: ['custom-video-row'],
    };
    videoRows.push(row);
    let needNewRow = true;
    details.forEach((detail) => {
      detail.url = oriUrl;
      const action = materialToAction(detail);
      needNewRow = true;
      for (let i = 0; i < videoRows.length; i++) {
        const row = videoRows[i];
        if (row.actions.length === 0) {
          row.trackIndex = action.trackIndex;
          row.hide = action.hide;
          row.classNames?.push(row.hide ? 'opacity' : '');
          row.actions.push(action);
          needNewRow = false;
          break;
        } else {
          if (row.hide === action.hide && row.trackIndex === action.trackIndex) {
            row.actions.push(action);
            needNewRow = false;
            break;
          }
        }
      }
      if (needNewRow) {
        videoRows.push({
          id: generateId(),
          type: RowType.video,
          actions: [action],
          trackIndex: action.trackIndex,
          hide: action.hide,
          rowHeight: 42,
          classNames: ['custom-video-row', action.hide ? 'opacity' : ''],
        });
      }
    });
  }
  videoRows.sort((a, b) => a.trackIndex! - b.trackIndex!);
  return videoRows;
}

export function transformMusic(music: Material) {
  const musicRows: CustomRow[] = [];
  const { details } = music;
  if (details && details.length > 0) {
    const row: CustomRow = {
      id: generateId(),
      type: RowType.music,
      actions: [],
      rowHeight: 42,
      classNames: ['custom-music-row'],
      mute: details[0]?.mute || false,
    };
    musicRows.push(row);
    let needNewRow = true;
    details.forEach((detail) => {
      const action = materialToAction(detail);
      needNewRow = true;
      for (let i = 0; i < musicRows.length; i++) {
        const row = musicRows[i];
        if (row.actions.length === 0) {
          row.trackIndex = action.trackIndex;
          row.volume = action.volume;
          row.classNames?.push(row.mute ? 'opacity' : '');
          row.actions.push(action);
          needNewRow = false;
          break;
        } else {
          if (row.mute === action.mute && row.trackIndex === action.trackIndex) {
            row.actions.push(action);
            needNewRow = false;
            break;
          }
        }
      }
      if (needNewRow) {
        musicRows.push({
          id: generateId(),
          type: RowType.music,
          actions: [action],
          trackIndex: action.trackIndex,
          volume: action.volume,
          rowHeight: 42,
          classNames: ['custom-music-row', action.mute ? 'opacity' : ''],
          mute: action?.mute || false,
        });
      }
    });
  }
  musicRows.sort((a, b) => a.trackIndex! - b.trackIndex!);
  return musicRows;
}

/**
 * 创建原声、原声字幕、翻译、翻译字幕
 * @param audio 原声、翻译物料
 * @param mode 声音模式
 */
export function transformAudio(audio: Material, mode: AudioMode, projectMode: ProjectMode) {
  const audioRows: CustomRow[] = [];
  const subtitleRows: CustomRow[] = [];
  const { details } = audio;
  if (details) {
    const audioRow: CustomRow = {
      id: generateId(),
      type: mode === AudioMode.Translation ? RowType.translation : RowType.audio,
      actions: [],
      rowHeight: 26,
      classNames: ['custom-audio-row'],
      volume: 0.5,
      mute: details[0]?.mute || false,
    };
    audioRows.push(audioRow);
    const subtitleRow: CustomRow = {
      id: generateId(),
      type: mode === AudioMode.Translation ? RowType.translationSubtitle : RowType.audioSubtitle,
      actions: [],
      rowHeight: 20,
      classNames: ['custom-subtitle-row'],
      hide: false,
    };
    subtitleRows.push(subtitleRow);
    let needNewRow = true;
    details.forEach((detail) => {
      const action = materialToAction(detail, mode);
      needNewRow = true;
      for (let i = 0; i < audioRows.length; i++) {
        const row = audioRows[i];
        if (row.actions.length === 0) {
          row.trackIndex = action.trackIndex;
          row.volume = action.volume;
          row.classNames?.push(row.mute ? 'opacity' : '');
          row.actions.push(action);
          needNewRow = false;
          break;
        } else {
          if (row.mute === action.mute && row.trackIndex === action.trackIndex) {
            row.actions.push(action);
            needNewRow = false;
            break;
          }
        }
      }
      if (needNewRow) {
        audioRows.push({
          id: generateId(),
          type: mode === AudioMode.Translation ? RowType.translation : RowType.audio,
          actions: [action],
          trackIndex: action.trackIndex,
          volume: action.volume,
          rowHeight: 26,
          classNames: ['custom-audio-row', action.mute ? 'opacity' : ''],
          mute: action?.mute || false,
        });
      }
      detail.subtitleSegmented?.forEach((sub: SubtitleSegmented, index) => {
        const action = {
          ...sub,
          id: detail.id,
          wordId: sub.id,
          type: 3,
        };
        const subtitleSegmentedAction = materialToAction(
          action as unknown as MaterialDetail,
          mode,
          projectMode,
        );
        subtitleRow.actions.push({
          ...subtitleSegmentedAction,
          subtitle_start: detail.subtitle.timeLineFrom / 1000,
          subtitle_end: detail.subtitle.timeLineTo / 1000,
          index,
        });
      });
    });
  }
  audioRows.sort((a, b) => a.trackIndex! - b.trackIndex!);
  return {
    audios: audioRows,
    subtitles: subtitleRows,
  };
}

export function transformMaterialListToRowData(
  videoMaterial: Material,
  musicMaterial: Material,
  sentenceMaterial: Material,
  translationMaterial: Material,
  mode: ProjectMode,
) {
  let rows: CustomRow[] = [];
  if (videoMaterial.details) {
    const videoRows = transformVideos(videoMaterial);
    rows.push(...videoRows.reverse());
  }
  if (musicMaterial.details) {
    const musicRows = transformMusic(musicMaterial);
    rows.push(...musicRows.reverse());
  }
  if (showAudioAndTranslation(mode)) {
    if (sentenceMaterial.details) {
      const { audios } = transformAudio(sentenceMaterial, AudioMode.OriInTrans, mode);
      rows.push(...audios.reverse());
    }
    if (translationMaterial.details) {
      const { audios, subtitles } = transformAudio(
        translationMaterial,
        AudioMode.Translation,
        mode,
      );
      rows.unshift(...subtitles);
      rows.push(...audios.reverse());
    }
  }
  // else if (showAudioAndDoubleSubtitle(mode) || isProjectModelE(mode)) {
  //   if (sentenceMaterial.details) {
  //     const { audios } = transformAudio(sentenceMaterial, AudioMode.Origin, mode);
  //     rows.push(...audios.reverse());
  //   }
  //   if (translationMaterial.details) {
  //     const { subtitles } = transformAudio(translationMaterial, AudioMode.Translation, mode);
  //     rows.unshift(...subtitles);
  //   }
  // }
  else {
    if (sentenceMaterial.details) {
      const { audios, subtitles } = transformAudio(sentenceMaterial, AudioMode.Origin, mode);
      rows.unshift(...subtitles);
      rows.push(...audios.reverse());
    }
  }
  // 模式D和模式E不展示句子
  if (canSubtitleMove(mode)) {
    rows = rows.filter((res) => res.type !== RowType.audio);
  }
  return rows;
}

const loadedAudio: Record<string, HTMLAudioElement> = {};
export async function preloadAudio(material: Material) {
  if (material?.details) {
    await Promise.allSettled<Promise<HTMLAudioElement>[]>(
      material.details
        .filter((sentence) => sentence.sentenceUrl || sentence.url)
        .map((sentence) => {
          let audio = loadedAudio[sentence.sentenceUrl || sentence.url];
          if (audio) {
            return Promise.resolve(audio);
          } else {
            audio = new Audio(sentence.sentenceUrl || sentence.url);
            return new Promise((resolve, reject) => {
              audio.oncanplaythrough = () => {
                resolve(audio);
              }; // 等待音频加载完成
              audio.onerror = () => {
                reject(new Error('音频加载失败')); // 捕获音频加载失败
              };
              audio.load(); // 预加载音频
            });
          }
        }),
    ).then((res) => {
      res.forEach((item) => {
        if (item.status === 'fulfilled') {
          loadedAudio[item.value.src] = item.value;
        }
      });
    });
  }
}

export async function fullDownloadAudio(material: Material) {
  if (material?.details) {
    await Promise.allSettled<Promise<HTMLAudioElement>[]>(
      material.details
        .filter((sentence) => sentence.sentenceUrl || sentence.url)
        .map((sentence) => {
          let audio = loadedAudio[sentence.sentenceUrl || sentence.url];
          if (audio) {
            return Promise.resolve(audio);
          } else {
            return new Promise((resolve, reject) => {
              fetch(sentence.sentenceUrl || sentence.url)
                .then((response) => response.blob())
                .then((audioBlob) => {
                  const objectURL = URL.createObjectURL(audioBlob);
                  audio = new Audio(objectURL);
                  audio.oncanplaythrough = () => {
                    audio.title = sentence.sentenceUrl || sentence.url;
                    resolve(audio);
                  }; // 等待音频加载完成
                  audio.load();
                })
                .catch(() => {
                  reject(new Error('音频加载失败'));
                });
            });
          }
        }),
    ).then((res) => {
      res.forEach((item) => {
        if (item.status === 'fulfilled') {
          loadedAudio[item.value.src] = item.value;
          loadedAudio[item.value.title] = item.value;
        }
      });
    });
  }
}

export function checkPreloadedAudio(src: string) {
  return loadedAudio[src];
}

const waveImage: Record<
  string,
  {
    imgw: number;
    image: string;
    duration: number;
  }
> = {};
export async function transformWaveImage(material: Material, imgw: number) {
  const el = document.createElement('div');
  el.style.cssText = `
    width:${imgw}px;
    height: 72px;
    position: fixed;
    top: 0;
    z-index: -1;
    visibility: hidden;
  `;
  document.body.appendChild(el);
  const wavesurfer = WaveSurfer.create({
    container: el,
    waveColor: '#00000050', // 波形颜色
    barWidth: 2, // 波形条宽度
    barGap: 1, // 波形条间距
    cursorColor: 'transparent', // 隐藏光标
    height: 24, // 波形高度
    interact: false, // 禁用交互（如点击跳转）
    hideScrollbar: true, // 关键参数：禁用滚动条
  });

  for (let i = 0; i < material.details.length; i++) {
    const detail = material.details[i];
    // if (detail.url in waveImage) {
    //   continue;
    // }
    waveImage[detail.url] = {
      image: '',
      duration: 0,
      imgw,
    };
    const blobBGMUrl = checkPreloadedBGM(detail.url);
    await new Promise<void>((resolve) => {
      wavesurfer.load(blobBGMUrl);
      wavesurfer.on('ready', function () {
        wavesurfer.exportImage('image/webp', 0.8, 'dataURL').then((res) => {
          waveImage[detail.url].image = res[0];
          waveImage[detail.url].duration = wavesurfer.getDuration();
          resolve();
        });
      });
    });
  }
  wavesurfer.destroy();
  el.remove();
}

export function getWaveImage(src: string) {
  return waveImage[src];
}

const TrackIndexMap: Map<number, number> = new Map();
export function addTrackIndex(trackIndex: number) {
  const entries = [...TrackIndexMap.entries()];
  entries.sort((a, b) => a[0] - b[0]);
  if (entries.length === 0) {
    TrackIndexMap.set(trackIndex, 1);
    return;
  }
  let addedFlag = false;
  for (let i = 0; i < entries.length; i++) {
    const [existingTrackIndex, zIndex] = entries[i];
    if (existingTrackIndex > trackIndex) {
      if (!addedFlag) {
        addedFlag = true;
        TrackIndexMap.set(trackIndex, zIndex);
      }
      TrackIndexMap.set(existingTrackIndex, zIndex + 1);
    }
  }
  if (!addedFlag) {
    const lastEntry = entries.at(-1)!;
    if (lastEntry && lastEntry[1]) {
      TrackIndexMap.set(trackIndex, lastEntry[1] + 1);
    }
  }
}

export function getZIndex(trackIndex: number) {
  return TrackIndexMap.get(trackIndex);
}

let audition: { id: number | null; isTranslation: boolean };
export function setAuditionAction(info: { id?: number | null; isTranslation?: boolean }) {
  audition = { ...audition, ...info };
}
export function getAuditionActionId() {
  return audition.id;
}
export function checkoutVolume(id: number, type: number, volume: number, mute: boolean) {
  let resoultVolume: number;
  if (audition.id) {
    const isCanListen =
      id === audition.id &&
      ((audition.isTranslation && type === RowType.translation) ||
        (!audition.isTranslation && type === RowType.audio));
    if (isCanListen) {
      resoultVolume = volume;
    } else {
      resoultVolume = 0;
    }
  } else {
    resoultVolume = mute ? 0 : volume;
  }
  return resoultVolume;
}

// 下载背景音乐
const loadedBGM: Record<string, string> = {};
export function checkPreloadedBGM(src: string) {
  return loadedBGM[src];
}
export async function fullDownloadBGM(material: Material) {
  const BGMUrl = material.details[0].url;
  if (BGMUrl) {
    return new Promise<void>((resolve, reject) => {
      fetch(BGMUrl)
        .then((response) => response.blob())
        .then((audioBlob) => {
          const objectURL = URL.createObjectURL(audioBlob);
          loadedBGM[BGMUrl] = objectURL;
          resolve();
        })
        .catch(() => {
          reject(new Error('音频加载失败'));
        });
    });
  } else {
    return false;
  }
}
